import { selectUser } from 'features/auth/store/authSelectors';
import {
  callableErrorDetails,
  convertToApplicationError,
} from 'features/error-handling/errorUtils';
import { getTraceProperties } from 'features/firebase/firebase.utils';
import { logger } from 'features/logger/logger';
import { blockFirstQuestionVital } from 'features/monitoring/firstQuestionVital';
import {
  selectOptionalParticipationDocData,
  selectParticipationDocData,
} from 'features/participations/store/participationSelectors';
import { RoutePath } from 'features/routing/constants';
import { selectTraceInfo } from 'features/system-info/store/systemInfoSelectors';
import { getTimezoneName } from 'shared/utils/dateUtils';
import {
  errorOccurred,
  initializationDone,
  resumeSubmissionStarted,
  setLoading,
} from './questionnaireSlice';
import {
  enterPostQuestionnairePhase,
  resolveFinishData,
  submitQuestionnaireToServer,
} from './questionnaireSubmissionSteps';
import { redirectOrNavigateAfterCompletion } from './redirectOrNavigateAfterCompletion';
import type { DbTask } from '@hub/common';
import type { AppPromiseThunk } from 'features/store/store';

/**
 * Re-drives an interrupted submission from the frozen task document: re-invokes
 * the server submit, then reproduces the terminal outcome client-side from the
 * same frozen answers the server rendered.
 */
export const questionnaireResumeSubmissionThunk =
  (loadedTask: DbTask): AppPromiseThunk =>
  async (dispatch, getState) => {
    blockFirstQuestionVital();
    const { contextData } = loadedTask;
    if (!contextData) {
      const protocolId =
        selectOptionalParticipationDocData(getState())?.protocolId;
      const error = new Error(
        'Cannot resume submission — PROSE context data is missing'
      );
      logger.error(
        'Error in questionnaireResumeSubmissionThunk',
        { details: { activityName: loadedTask.activityName, protocolId } },
        error
      );
      const errorToLog = convertToApplicationError(error);
      dispatch(errorOccurred(errorToLog.toErrorData()));
      return;
    }

    // resumeInFlight is raised synchronously (before the first await) so a
    // concurrent snapshot-driven render sees the resume already in progress and
    // does not dispatch a second one.
    dispatch(resumeSubmissionStarted());

    // Readiness must be set before the server call: the mid-flight status
    // snapshot must not re-fire the page init effect, and the sync layer's
    // same-window skip absorbs the write echoes without a PROSE re-render.
    dispatch(initializationDone());
    dispatch(setLoading(true));

    const user = selectUser(getState());
    const participation = selectParticipationDocData(getState());
    const traceInfo = selectTraceInfo(getState());
    const now = new Date();
    const userTimezone = getTimezoneName();
    const traceProperties = getTraceProperties({ now, ...traceInfo });

    try {
      // The server submit runs before the client submission render: the server
      // owns the outcome and dedupes the re-invocation, so the client render
      // only reproduces the already-committed result for display.
      await submitQuestionnaireToServer({
        participationId: participation.id,
        activityName: loadedTask.activityName,
        traceProperties,
      });

      const { result } = await resolveFinishData(
        getState(),
        loadedTask,
        contextData,
        now,
        userTimezone
      );

      if (result.type === 'postQuestionnaire') {
        await enterPostQuestionnairePhase(dispatch, {
          userId: user.uid,
          participationId: participation.id,
          activityName: loadedTask.activityName,
          output: result.output,
          traceProperties,
        });
        return;
      }

      if (result.type === 'questionnaire') {
        logger.error(
          'questionnaireResumeSubmissionThunk: PROSE returned a questionnaire result after the server accepted the submission'
        );
      }

      redirectOrNavigateAfterCompletion({
        completionAction:
          result.type === 'completed'
            ? result.output.completionAction
            : undefined,
        redirectUrls: loadedTask.redirectUrls,
        questionnaireId: loadedTask.id,
        userId: user.uid,
        participantId: user.participantId,
        participationId: participation.id,
        activityName: loadedTask.activityName,
        dispatch,
        backToRoute: RoutePath.Home(participation.id),
      });
    } catch (error) {
      logger.error(
        'Error in questionnaireResumeSubmissionThunk',
        {
          details: {
            activityName: loadedTask.activityName,
            protocolId: participation.protocolId,
            ...callableErrorDetails(error),
          },
        },
        error as Error
      );
      const errorToLog = convertToApplicationError(error);
      dispatch(errorOccurred(errorToLog.toErrorData()));
      dispatch(setLoading(false));
    }
  };
